// Famille, amis, fêtes et coups de main.
import type { EventDef } from '../events';
import type { GameState } from '../../engine/state';
import { between, simpleEvent, working } from './kit';

const ALL_STAGES: EventDef['stages'] = ['prologue', 'stage1', 'stage2'];
const ADULT: EventDef['stages'] = ['stage1', 'stage2'];

/** Un montant au hasard, plus petit pendant les études qu'une fois dans la vie active. */
function amount(state: GameState, student: [number, number], adult: [number, number]): number {
  const [min, max] = state.stage === 'prologue' ? student : adult;
  return between(state, min, max);
}

/** Jours de jeu écoulés depuis la dernière fois qu'un événement est arrivé. */
function daysSince(state: GameState, id: string): number {
  const day = state.eventLastDays[id];
  return day === undefined ? Infinity : state.day - day;
}

/** Un logement à soi (colocation ou studio). */
function ownPlace(state: GameState): boolean {
  return state.housing !== 'parents';
}

// L'amour : un premier rendez-vous réussi pose un repère ; la première histoire peut finir
// par une rupture, la suivante (après une nouvelle rencontre) non.

/** Une histoire d'amour est en cours. */
function inLove(state: GameState): boolean {
  if (state.flags.amourSuivant) return true;
  return state.flags.amourPremier === true && !state.eventCounts.rupture_amoureuse;
}

/** Le repère posé par un premier rendez-vous réussi : la première histoire, ou la suivante. */
function loveFlag(state: GameState): string {
  return state.flags.amourPremier ? 'amourSuivant' : 'amourPremier';
}

/** Le prêt à Léo date d'au moins trois mois : il rembourse, ou pas encore. */
function leoOwes(state: GameState): boolean {
  return state.flags.pretLeo === true && daysSince(state, 'pret_leo') >= 90;
}

/** La caution pour Hugo court depuis six mois au moins : il déménage sans dette, ou ne paie plus. */
function cautionRunning(state: GameState): boolean {
  return (
    state.flags.cautionHugo === true &&
    !state.eventCounts.caution_hugo_impayes &&
    !state.eventCounts.caution_hugo_fin &&
    daysSince(state, 'caution_hugo') >= 180
  );
}

export const EVENTS: EventDef[] = [
  // Les fêtes de l'année
  simpleEvent({
    id: 'cadeaux_noel_famille',
    kind: 'proches',
    stages: ALL_STAGES,
    season: [12],
    cooldownDays: 300,
    params: (state) => ({ high: amount(state, [100, 160], [220, 350]), one: amount(state, [20, 30], [30, 50]) }),
    choices: [
      { id: 'gater', effects: (_, p) => ({ cash: -p.high, wellbeing: 4 }) },
      { id: 'tirage', effects: (_, p) => ({ cash: -p.one, wellbeing: 2 }) },
      { id: 'fait_main', effects: { energy: -5, wellbeing: 3 } },
    ],
  }),
  simpleEvent({
    id: 'reveillon_nouvel_an',
    kind: 'tentation',
    pleasureCategory: 'voyages',
    stages: ALL_STAGES,
    season: [12],
    cooldownDays: 300,
    params: (state) => ({ cost: amount(state, [50, 80], [90, 150]), small: amount(state, [8, 12], [12, 20]) }),
    choices: [
      { id: 'gite', effects: (_, p) => ({ cash: -p.cost, pleasure: 5, energy: -3 }) },
      { id: 'sarah', effects: (_, p) => ({ cash: -p.small, wellbeing: 2 }) },
      { id: 'dormir' },
    ],
  }),
  simpleEvent({
    id: 'saint_valentin',
    kind: 'proches',
    pleasureCategory: 'repas',
    stages: ALL_STAGES,
    season: [2],
    cooldownDays: 300,
    condition: inLove,
    params: (state) => ({ cost: amount(state, [50, 70], [80, 120]) }),
    choices: [
      { id: 'restaurant', effects: (_, p) => ({ cash: -p.cost, pleasure: 4 }) },
      { id: 'chandelles', effects: { energy: -3, wellbeing: 3 } },
      { id: 'autre_soir', effects: { wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'fete_des_meres',
    kind: 'proches',
    stages: ALL_STAGES,
    season: [5],
    cooldownDays: 300,
    params: (state) => ({ cost: amount(state, [40, 60], [60, 90]), small: amount(state, [15, 20], [20, 30]) }),
    choices: [
      { id: 'coffret', effects: (_, p) => ({ cash: -p.cost, wellbeing: 3 }) },
      { id: 'fleurs', effects: (_, p) => ({ cash: -p.small, wellbeing: 2 }) },
      { id: 'cuisiner', effects: { energy: -3, wellbeing: 3 } },
    ],
  }),
  simpleEvent({
    id: 'fete_des_peres',
    kind: 'proches',
    stages: ALL_STAGES,
    season: [6],
    cooldownDays: 300,
    params: (state) => {
      const ticket = amount(state, [35, 50], [50, 80]);
      return { ticket, both: ticket * 2 };
    },
    choices: [
      { id: 'place', effects: (_, p) => ({ cash: -p.ticket, wellbeing: 2 }) },
      { id: 'ensemble', effects: (_, p) => ({ cash: -p.both, wellbeing: 5, energy: -2 }) },
      { id: 'appel', effects: { wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'reunion_famille',
    kind: 'proches',
    stages: ALL_STAGES,
    season: [7, 8],
    cooldownDays: 300,
    params: (state) => ({ cost: amount(state, [30, 50], [60, 100]), day: amount(state, [10, 20], [20, 35]) }),
    choices: [
      { id: 'weekend', effects: (_, p) => ({ cash: -p.cost, wellbeing: 4, energy: -2 }) },
      { id: 'dimanche', effects: (_, p) => ({ cash: -p.day, wellbeing: 2 }) },
      { id: 'excuse', effects: { wellbeing: -2 } },
    ],
  }),

  // Les amis : anniversaires, soirées, services rendus
  simpleEvent({
    id: 'anniversaire_leo',
    kind: 'proches',
    stages: ALL_STAGES,
    maxCount: 2,
    params: (state) => ({ part: amount(state, [10, 20], [20, 35]), cost: amount(state, [30, 50], [50, 90]) }),
    choices: [
      { id: 'cagnotte', effects: (_, p) => ({ cash: -p.part, wellbeing: 2 }) },
      { id: 'cadeau', effects: (_, p) => ({ cash: -p.cost, wellbeing: 3 }) },
      { id: 'fait_main', effects: { energy: -4, wellbeing: 3 } },
    ],
  }),
  simpleEvent({
    id: 'cremaillere_chez_toi',
    kind: 'proches',
    stages: ALL_STAGES,
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({ cost: amount(state, [60, 100], [120, 200]) }),
    choices: [
      { id: 'buffet', effects: (_, p) => ({ cash: -p.cost, wellbeing: 5, energy: -3 }) },
      { id: 'auberge', effects: { energy: -2, wellbeing: 3 } },
      { id: 'repousser', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'soiree_jeux_karim',
    kind: 'tentation',
    pleasureCategory: 'loisirs',
    stages: ALL_STAGES,
    maxCount: 2,
    params: (state) => ({ part: amount(state, [8, 12], [10, 15]), price: amount(state, [40, 50], [45, 60]) }),
    choices: [
      { id: 'partager', effects: (_, p) => ({ cash: -p.part, pleasure: 2 }) },
      { id: 'acheter', effects: (_, p) => ({ cash: -p.price, pleasure: 3 }) },
      { id: 'cartes', effects: { wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'resto_sarah',
    kind: 'tentation',
    pleasureCategory: 'repas',
    stages: ADULT,
    maxCount: 1,
    params: (state) => ({ cost: between(state, 60, 90), dessert: between(state, 10, 15) }),
    choices: [
      { id: 'aller', effects: (_, p) => ({ cash: -p.cost, pleasure: 4 }) },
      { id: 'dessert', effects: (_, p) => ({ cash: -p.dessert, wellbeing: 1 }) },
      { id: 'decliner', effects: { wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    id: 'enterrement_vie_celibataire',
    kind: 'tentation',
    pleasureCategory: 'voyages',
    stages: ADULT,
    maxCount: 1,
    params: (state) => ({ cost: between(state, 250, 400), soiree: between(state, 40, 70) }),
    choices: [
      { id: 'weekend', effects: (_, p) => ({ cash: -p.cost, pleasure: 5, energy: -4 }) },
      { id: 'soiree', effects: (_, p) => ({ cash: -p.soiree, wellbeing: 2, energy: -1 }) },
      { id: 'decliner', effects: { wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    id: 'bougies_sarah',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ALL_STAGES,
    maxCount: 2,
    params: (state) => ({ cost: amount(state, [30, 45], [45, 70]), small: amount(state, [8, 12], [10, 15]) }),
    choices: [
      { id: 'coffret', effects: (_, p) => ({ cash: -p.cost, pleasure: 2 }) },
      { id: 'bougie', effects: (_, p) => ({ cash: -p.small, wellbeing: 1 }) },
      { id: 'rien', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'club_escalade',
    kind: 'tentation',
    stages: ALL_STAGES,
    maxCount: 1,
    params: (state) => ({ monthly: amount(state, [20, 28], [30, 40]) }),
    choices: [
      {
        id: 'inscrire',
        effects: (_, p) => ({ subscribe: { id: 'escalade', amount: p.monthly }, wellbeing: 3 }),
      },
      {
        id: 'essai',
        effects: { energy: -2, wellbeing: 1, followUp: { event: 'club_escalade_essai', days: 7 } },
      },
      { id: 'non' },
    ],
  }),
  simpleEvent({
    // Une semaine après la séance d'essai : s'inscrire, ou en rester là.
    id: 'club_escalade_essai',
    kind: 'tentation',
    stages: ALL_STAGES,
    manual: true,
    choices: [
      {
        id: 'inscrire',
        effects: (_, p) => ({ subscribe: { id: 'escalade', amount: p.monthly }, wellbeing: 3 }),
      },
      { id: 'arreter' },
    ],
  }),
  simpleEvent({
    id: 'raclette_en_double',
    kind: 'chance',
    stages: ALL_STAGES,
    maxCount: 1,
    params: (state) => ({ gain: between(state, 25, 40) }),
    choices: [
      { id: 'revendre', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'soiree', effects: { energy: -2, wellbeing: 3 } },
      { id: 'offrir', effects: { wellbeing: 2 } },
    ],
  }),
  simpleEvent({
    id: 'hebergement_leo',
    kind: 'proches',
    stages: ALL_STAGES,
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({ food: amount(state, [25, 40], [40, 70]) }),
    choices: [
      { id: 'accueillir', effects: (_, p) => ({ cash: -p.food, wellbeing: 3, energy: -2 }) },
      { id: 'partager', effects: { energy: -2, wellbeing: 1 } },
      { id: 'refuser', effects: { wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    id: 'voiture_karim',
    kind: 'proches',
    stages: ALL_STAGES,
    maxCount: 2,
    condition: (state) => state.hasCar,
    params: (state) => ({ fuel: between(state, 60, 80) }),
    choices: [
      { id: 'preter', effects: { energy: -3, wellbeing: 2 } },
      { id: 'plein', effects: (_, p) => ({ cash: p.fuel, energy: -3, wellbeing: 1 }) },
      { id: 'refuser', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'karim_chomage',
    kind: 'proches',
    stages: ADULT,
    maxCount: 1,
    params: (state) => ({ cost: between(state, 60, 100) }),
    choices: [
      { id: 'diners', effects: (_, p) => ({ cash: -p.cost, wellbeing: 2 }) },
      { id: 'cv', effects: { energy: -3, wellbeing: 2, xp: { skill: 'communication', amount: 10 } } },
      { id: 'message' },
    ],
  }),
  simpleEvent({
    id: 'camion_karim',
    kind: 'proches',
    pleasureCategory: 'repas',
    stages: ADULT,
    maxCount: 1,
    params: (state) => ({ small: between(state, 20, 30), big: between(state, 100, 150) }),
    choices: [
      { id: 'petit', effects: (_, p) => ({ cash: -p.small, pleasure: 2 }) },
      { id: 'grand', effects: (_, p) => ({ cash: -p.big, wellbeing: 3 }) },
      { id: 'partager', effects: { wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'karim_rembourse',
    kind: 'chance',
    stages: ALL_STAGES,
    maxCount: 1,
    params: (state) => ({ gain: amount(state, [30, 50], [40, 80]) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'refuser', effects: { wellbeing: 2 } },
    ],
  }),

  // Le prêt à Léo, et ses suites
  simpleEvent({
    id: 'pret_leo',
    kind: 'proches',
    stages: ALL_STAGES,
    maxCount: 1,
    params: () => ({ amount: 150, gift: 40 }),
    choices: [
      { id: 'preter', effects: (_, p) => ({ cash: -p.amount, wellbeing: 1, flag: 'pretLeo' }) },
      { id: 'donner', effects: (_, p) => ({ cash: -p.gift, wellbeing: 2 }) },
      { id: 'refuser', effects: { wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    id: 'pret_leo_rembourse',
    kind: 'chance',
    pleasureCategory: 'repas',
    stages: ALL_STAGES,
    // Une suite : plus lourde au tirage pour arriver dans les mois qui suivent.
    weight: 3,
    maxCount: 1,
    condition: (state) =>
      leoOwes(state) &&
      !state.flags.pretLeoRegle &&
      !state.flags.pretLeoEfface &&
      (!state.eventCounts.pret_leo_retard || daysSince(state, 'pret_leo_retard') >= 45),
    params: () => ({ amount: 150, resto: 40 }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.amount, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.amount }) },
      { id: 'resto', effects: (_, p) => ({ cash: p.amount - p.resto, pleasure: 3 }) },
    ],
  }),
  simpleEvent({
    id: 'pret_leo_retard',
    kind: 'proches',
    stages: ALL_STAGES,
    weight: 3,
    maxCount: 1,
    condition: (state) => leoOwes(state) && !state.eventCounts.pret_leo_rembourse,
    params: () => ({ amount: 150 }),
    choices: [
      { id: 'rappeler', effects: (_, p) => ({ cash: p.amount, wellbeing: -1, flag: 'pretLeoRegle' }) },
      { id: 'attendre', effects: { wellbeing: -1 } },
      { id: 'effacer', effects: { wellbeing: 1, flag: 'pretLeoEfface' } },
    ],
  }),

  // La caution pour Hugo, et ses suites
  simpleEvent({
    id: 'caution_hugo',
    kind: 'proches',
    stages: ADULT,
    maxCount: 1,
    condition: working,
    params: () => ({ rent: 550 }),
    choices: [
      { id: 'signer', effects: { wellbeing: 2, flag: 'cautionHugo' } },
      { id: 'garantie', effects: { energy: -2, wellbeing: 1 } },
      { id: 'refuser', effects: { wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    id: 'caution_hugo_fin',
    kind: 'proches',
    stages: ADULT,
    // Deux fois sur trois environ, tout se passe bien.
    weight: 2,
    maxCount: 1,
    condition: cautionRunning,
    params: (state) => ({ plant: between(state, 25, 45) }),
    choices: [
      { id: 'plante', effects: (_, p) => ({ cash: -p.plant, wellbeing: 2 }) },
      { id: 'cartons', effects: { energy: -4, wellbeing: 3 } },
      { id: 'passer', effects: { wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'caution_hugo_impayes',
    kind: 'imprevu',
    stages: ADULT,
    bad: true,
    shock: 7,
    maxCount: 1,
    condition: cautionRunning,
    params: () => ({ cost: 1100 }),
    page: 'fonds_urgence',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'compte', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'credit', effects: (_, p) => ({ loan: { amount: p.cost, rate: 0.09, months: 12 }, wellbeing: -1 }) },
    ],
  }),

  // L'amour
  simpleEvent({
    id: 'premier_rendez_vous',
    kind: 'proches',
    pleasureCategory: 'repas',
    stages: ALL_STAGES,
    maxCount: 2,
    condition: (state) => !inLove(state),
    params: (state) => ({ cost: amount(state, [30, 50], [50, 80]) }),
    choices: [
      { id: 'restaurant', effects: (state, p) => ({ cash: -p.cost, pleasure: 3, flag: loveFlag(state) }) },
      { id: 'pique_nique', effects: (state) => ({ energy: -2, wellbeing: 3, flag: loveFlag(state) }) },
      { id: 'annuler' },
    ],
  }),
  simpleEvent({
    id: 'rupture_amoureuse',
    kind: 'proches',
    stages: ALL_STAGES,
    bad: true,
    shock: 8,
    maxCount: 1,
    condition: (state) =>
      state.flags.amourPremier === true &&
      !state.flags.amourSuivant &&
      daysSince(state, 'premier_rendez_vous') >= 150,
    params: (state) => ({ cost: amount(state, [60, 100], [120, 200]) }),
    choices: [
      { id: 'amis', effects: { energy: -2, wellbeing: 2 } },
      { id: 'air', effects: (_, p) => ({ cash: -p.cost, wellbeing: 4 }) },
      { id: 'travail', effects: (state) => ({ xp: { skill: state.focusSkill, amount: 20 }, wellbeing: -1 }) },
    ],
  }),

  // La famille
  simpleEvent({
    id: 'coup_de_pouce_parents',
    kind: 'chance',
    pleasureCategory: 'achats',
    stages: ALL_STAGES,
    maxCount: 2,
    condition: (state) => state.milieu === 'aise',
    params: (state) => ({ gain: amount(state, [150, 250], [300, 450]) }),
    page: 'loin_des_yeux',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'plaisir', effects: { pleasure: 4 } },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
    ],
  }),
  simpleEvent({
    id: 'parents_besoin_aide',
    kind: 'proches',
    stages: ADULT,
    maxCount: 2,
    condition: (state) => state.milieu === 'modeste' && working(state),
    params: (state) => {
      const cost = between(state, 250, 400);
      return { cost, half: Math.round(cost / 10) * 5 };
    },
    choices: [
      { id: 'donner', effects: (_, p) => ({ cash: -p.cost, wellbeing: 3 }) },
      { id: 'moitie', effects: (_, p) => ({ cash: -p.half, wellbeing: 1 }) },
      { id: 'echelonner', effects: { energy: -2, wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'demenagement_parents',
    kind: 'proches',
    stages: ALL_STAGES,
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({ cost: amount(state, [120, 160], [180, 250]) }),
    choices: [
      { id: 'porter', effects: { energy: -5, wellbeing: 3 } },
      { id: 'demenageurs', effects: (_, p) => ({ cash: -p.cost, wellbeing: 2 }) },
      { id: 'pizza', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'chambre_enfance',
    kind: 'chance',
    stages: ALL_STAGES,
    maxCount: 1,
    // Tes parents ont encore la maison de ton enfance.
    condition: (state) => ownPlace(state) && !state.eventCounts.demenagement_parents,
    params: (state) => ({ gain: between(state, 80, 200) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'vendre', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'garder', effects: { wellbeing: 2 } },
    ],
  }),
  simpleEvent({
    id: 'naissance_hugo',
    kind: 'proches',
    stages: ADULT,
    maxCount: 1,
    params: (state) => ({ cost: between(state, 100, 160), gift: between(state, 50, 100) }),
    choices: [
      { id: 'liste', effects: (_, p) => ({ cash: -p.cost, wellbeing: 3 }) },
      { id: 'livret', effects: (_, p) => ({ cash: -p.gift, wellbeing: 2 }) },
      { id: 'doudou', effects: { energy: -3, wellbeing: 3 } },
    ],
  }),

  // Suzanne
  simpleEvent({
    id: 'enveloppe_suzanne',
    kind: 'chance',
    pleasureCategory: 'achats',
    stages: ALL_STAGES,
    maxCount: 3,
    params: (state) => ({ gain: amount(state, [40, 80], [60, 120]), tea: amount(state, [10, 15], [15, 20]) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'plaisir', effects: { pleasure: 3 } },
      { id: 'salon', effects: (_, p) => ({ cash: p.gain - p.tea, wellbeing: 3 }) },
    ],
  }),
  simpleEvent({
    id: 'anniversaire_suzanne',
    kind: 'proches',
    stages: ALL_STAGES,
    maxCount: 3,
    params: (state) => ({ cost: amount(state, [25, 40], [40, 70]) }),
    choices: [
      { id: 'chale', effects: (_, p) => ({ cash: -p.cost, wellbeing: 3 }) },
      { id: 'album', effects: { energy: -4, wellbeing: 4 } },
      { id: 'appel', effects: { wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'telephone_suzanne',
    kind: 'proches',
    stages: ALL_STAGES,
    maxCount: 1,
    choices: [
      { id: 'apres_midi', effects: { energy: -3, wellbeing: 4 } },
      { id: 'guider', effects: { energy: -1, wellbeing: 2 } },
      { id: 'atelier', effects: { wellbeing: 1 } },
    ],
  }),

  // Donner un coup de main
  simpleEvent({
    id: 'collecte_alimentaire',
    kind: 'proches',
    stages: ALL_STAGES,
    maxCount: 3,
    params: (state) => ({ cost: amount(state, [10, 15], [15, 25]) }),
    choices: [
      { id: 'aider', effects: { energy: -3, wellbeing: 3, xp: { skill: 'communication', amount: 10 } } },
      { id: 'don', effects: (_, p) => ({ cash: -p.cost, wellbeing: 2 }) },
      { id: 'passer' },
    ],
  }),
  simpleEvent({
    id: 'voisin_de_palier',
    kind: 'proches',
    stages: ALL_STAGES,
    maxCount: 1,
    condition: ownPlace,
    choices: [
      { id: 'aider', effects: { energy: -2, wellbeing: 3 } },
      { id: 'livraison', effects: { energy: -1, wellbeing: 1 } },
      { id: 'saluer' },
    ],
  }),
  simpleEvent({
    id: 'course_solidaire',
    kind: 'proches',
    stages: ALL_STAGES,
    maxCount: 2,
    params: (state) => ({ gift: amount(state, [10, 20], [20, 40]) }),
    choices: [
      { id: 'donner', effects: (_, p) => ({ cash: -p.gift, wellbeing: 2 }) },
      { id: 'courir', effects: { energy: -4, wellbeing: 3 } },
      { id: 'partager', effects: { wellbeing: 1 } },
    ],
  }),

  // Les coups durs
  simpleEvent({
    id: 'verre_renverse',
    kind: 'imprevu',
    stages: ALL_STAGES,
    bad: true,
    shock: 6,
    maxCount: 1,
    params: (state) => ({ cost: amount(state, [200, 280], [250, 380]), franchise: between(state, 100, 130) }),
    page: 'fonds_urgence',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'carte', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'assurance', effects: (_, p) => ({ cash: -p.franchise, energy: -2, wellbeing: -1 }) },
    ],
  }),
  simpleEvent({
    id: 'pere_hopital',
    kind: 'imprevu',
    stages: ALL_STAGES,
    bad: true,
    shock: 7,
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({ cost: amount(state, [60, 90], [90, 140]), low: amount(state, [20, 30], [25, 40]) }),
    choices: [
      { id: 'train', effects: (_, p) => ({ cash: -p.cost, wellbeing: 1 }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost, wellbeing: 1 }) },
      { id: 'covoiturage', effects: (_, p) => ({ cash: -p.low, energy: -4 }) },
    ],
  }),
  simpleEvent({
    id: 'tapage_nocturne',
    kind: 'imprevu',
    stages: ALL_STAGES,
    bad: true,
    shock: 5,
    maxCount: 1,
    condition: ownPlace,
    // L'amende forfaitaire pour tapage nocturne, partagée entre cinq invités environ.
    params: (state) => {
      const gift = amount(state, [12, 18], [15, 25]);
      return { fine: 68, share: 14, total: 68 + gift };
    },
    choices: [
      { id: 'payer', effects: (_, p) => ({ cash: -p.fine }) },
      { id: 'excuses', effects: (_, p) => ({ cash: -p.total, wellbeing: 2 }) },
      { id: 'partager', effects: (_, p) => ({ cash: -p.share, wellbeing: -1 }) },
    ],
  }),
];
